import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Flame, TrendingUp } from 'lucide-react';
const TOTAL_MS = 30 * 60 * 1000; // 30 minutes
const START_PROGRESS = 77;
const MAX_PROGRESS = 94;
const STORAGE_KEY = 'urgency_end';
function getEnd(): number {
  const saved = sessionStorage.getItem(STORAGE_KEY);
  if (saved) {
    const end = parseInt(saved, 10);
    if (end > Date.now()) return end;
  }
  const end = Date.now() + TOTAL_MS;
  sessionStorage.setItem(STORAGE_KEY, end.toString());
  return end;
}
export function UrgencyBanner() {
  const [msLeft, setMsLeft] = useState<number>(() => getEnd() - Date.now());
  useEffect(() => {
    const interval = setInterval(() => {
      const remaining = getEnd() - Date.now();
      if (remaining <= 0) {
        // Reset the window so urgency stays evergreen
        const newEnd = Date.now() + TOTAL_MS;
        sessionStorage.setItem(STORAGE_KEY, newEnd.toString());
        setMsLeft(TOTAL_MS);
      } else {
        setMsLeft(remaining);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  const totalSeconds = Math.max(0, Math.floor(msLeft / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');
  const elapsedRatio = Math.min(1, Math.max(0, 1 - msLeft / TOTAL_MS));
  const progress = Math.round(START_PROGRESS + elapsedRatio * (MAX_PROGRESS - START_PROGRESS));
  return <motion.div initial={{
    opacity: 0,
    y: 12
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} className="max-w-2xl mx-auto mb-6 md:mb-8 rounded-2xl bg-red-600 text-white shadow-lg shadow-red-600/30 overflow-hidden border-2 border-red-400" data-id="element-206">
      <div className="p-3 md:p-4" data-id="element-207">
        {/* Top row: label + timer */}
        <div className="flex items-center justify-between gap-3 mb-2" data-id="element-208">
          <div className="flex items-center gap-2 min-w-0" data-id="element-209">
            <motion.span animate={{
            scale: [1, 1.2, 1]
          }} transition={{
            repeat: Infinity,
            duration: 1.2
          }} className="flex-shrink-0" data-id="element-210">
              <Flame className="w-5 h-5 md:w-6 md:h-6 text-amber-300" data-id="element-211" />
            </motion.span>
            <span className="font-extrabold uppercase tracking-wide text-xs md:text-base leading-tight" data-id="element-212">
              ¡Últimos cupos en oferta!
            </span>
          </div>

          <div className="flex-shrink-0 flex items-center gap-1 bg-red-800/60 rounded-lg px-2 py-1 md:px-3 md:py-1.5 tabular-nums" data-id="element-213">
            <motion.span animate={{
            opacity: [1, 0.4, 1]
          }} transition={{
            repeat: Infinity,
            duration: 1
          }} className="text-lg md:text-2xl font-extrabold" data-id="element-214">
              {pad(minutes)}:{pad(seconds)}
            </motion.span>
          </div>
        </div>

        {/* Message */}
        <p className="text-[11px] md:text-sm text-red-50 leading-snug mb-2.5 flex items-start gap-1.5" data-id="element-215">
          <TrendingUp className="w-3.5 h-3.5 md:w-4 md:h-4 flex-shrink-0 mt-0.5 text-amber-300" data-id="element-216" />
          <span data-id="element-217">
            Cuando se agoten los cupos, el precio{' '}
            <strong className="text-amber-300 font-bold" data-id="element-218">
              vuelve a su valor original
            </strong>{' '}
            y no vuelve a bajar.
          </span>
        </p>

        {/* Progress bar */}
        <div data-id="element-219">
          <div className="flex items-center justify-between text-[10px] md:text-xs font-semibold mb-1" data-id="element-220">
            <span className="text-red-100" data-id="element-221">Cupos reservados</span>
            <span className="text-white" data-id="element-222">{progress}%</span>
          </div>
          <div className="h-2.5 md:h-3 w-full bg-red-900/50 rounded-full overflow-hidden" role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100} aria-label="Cupos reservados" data-id="element-223">
            <motion.div initial={{
            width: `${START_PROGRESS}%`
          }} animate={{
            width: `${progress}%`
          }} transition={{
            duration: 0.6,
            ease: 'easeOut'
          }} className="h-full bg-amber-400 rounded-full" data-id="element-224" />
          </div>
        </div>
      </div>
    </motion.div>;
}